import type React from "react";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { cn } from "@/lib/utils";

export interface PageTabItem {
  value: string;
  label: string;
  count?: number;
  disabled?: boolean;
  /** id of an element explaining why the tab is disabled. */
  describedBy?: string;
}

export function PageTabs({
  items,
  density = "standard",
  action,
  className,
  children,
  ...props
}: React.ComponentProps<typeof Tabs> & {
  items: readonly PageTabItem[];
  density?: "standard" | "compact";
  action?: React.ReactNode;
}): React.ReactElement {
  const compact = density === "compact";

  return (
    <Tabs className={cn(compact ? "gap-4" : "gap-[18px]", className)} {...props}>
      <div className="flex items-center gap-3 border-b border-line">
        <TabsList
          className={cn(
            "min-w-0 flex-1 justify-start border-b-0 p-0",
            compact ? "h-[37px] gap-6" : "h-10 gap-7",
          )}
        >
          {items.map((item) => (
            <TabsTrigger
              key={item.value}
              value={item.value}
              disabled={item.disabled}
              aria-describedby={item.describedBy}
              className={cn(
                "grow-0 gap-0 px-0 font-normal data-active:font-medium",
                compact ? "h-[37px] text-[11px] leading-[14px]" : "h-10 text-[13px] leading-[18px]",
              )}
            >
              {item.label}
              {item.count !== undefined ? (
                compact ? (
                  <span className="pl-[5px] text-[9px] leading-3">{item.count}</span>
                ) : (
                  <Badge
                    variant="neutral"
                    size="compact"
                    className="ml-1.5 min-w-5 rounded-[8px] font-normal"
                  >
                    {item.count}
                  </Badge>
                )
              ) : null}
            </TabsTrigger>
          ))}
        </TabsList>
        {action ? <div className="shrink-0">{action}</div> : null}
      </div>
      {children}
    </Tabs>
  );
}
